import { useNavigate, useParams } from 'react-router-dom';
import FileSaver from 'file-saver';
import {
    Box,
    Flex,
    Heading,
    Text,
    Button,
    Icon,
    useToast,
    Select,
    chakra,
    Modal,
    ModalContent,
    ModalOverlay,
    ModalHeader,
    ModalFooter,
    ModalCloseButton,
    ModalBody,
    useDisclosure,
} from '@chakra-ui/react';

import PageHeader from 'layout/pageHeader';
import { TKeywordType } from 'types';
import MDropZone from 'components/molecules/MDropZone';
import { ChangeEvent, useRef, useState } from 'react';
import { EXCEL_FILE_TYPE } from 'shared/const';
import icons from 'shared/icons';
import { useLazyExportClientKeywordsQuery, useUploadClientKeywordsMutation } from 'core/API/keywordsApi';
import { intentionalyFloatingPromiseReturn } from 'utils/wrappers';
import { linkStyle } from 'shared/styles';
import { useGetKeywordsLimits } from 'core/redux/selectors/authSelectors';
import { isFetchBaseQueryError } from 'utils/erros';
import { useTranslation } from 'helpers/languages/translations';
import { IApiError } from 'shared/types';

const KeywordsUpload = () => {
    const [uploadedFile, setUploadedFile] = useState<File | null>(null);
    const [uploadKeywords, { isLoading }] = useUploadClientKeywordsMutation({
        fixedCacheKey: 'keywords-upload-key',
    });
    const [exportKeywords] = useLazyExportClientKeywordsQuery();
    const { bannerLimit, searchLimit } = useGetKeywordsLimits();
    const { isOpen, onOpen, onClose } = useDisclosure();

    const { page } = useParams();
    const navigate = useNavigate();
    const translate = useTranslation();
    const goBack = () => {
        navigate(-1);
    };
    const toast = useToast();
    const overwriteInitialRef = useRef(null);

    const [keywordsType, setKeywordsType] = useState<'banner' | 'search' | 'both'>(page as TKeywordType);
    const [uploadMethod, setUploadMethod] = useState<'keep' | 'overwrite'>('keep');

    const handleChangeFile = (file: File | null) => {
        if (file?.type === EXCEL_FILE_TYPE) {
            setUploadedFile(file);
        } else {
            if (file !== null)
                toast({
                    title: translate('error.upload.file-invalid-type.xlsx'),
                    status: 'error',
                    isClosable: true,
                });
            setUploadedFile(null);
        }
    };

    const handleChangeUploadMethod = (e: ChangeEvent<HTMLSelectElement>) => {
        setUploadMethod(e.target.value as 'keep' | 'overwrite');
    };

    const handleChangeKeywordType = (e: ChangeEvent<HTMLSelectElement>) => {
        setKeywordsType(e.target.value as TKeywordType | 'both');
    };

    // TODO: uncomment when master keywords are integrated
    const renderDescriptionSection = () => {
        const UploadIcon = icons.upload;

        const handleUploadKeywords = async () => {
            try {
                const bodyFormData = new FormData();
                bodyFormData.append('excelFile', uploadedFile as File);
                bodyFormData.append(
                    'keywordTypes',
                    JSON.stringify(keywordsType !== 'both' ? [keywordsType] : ['search', 'banner']),
                );
                bodyFormData.append('uploadMethod', uploadMethod);
                const res = await uploadKeywords(bodyFormData);
                if ('error' in res) {
                    const errorObj: IApiError = res.error as IApiError;
                    if (isFetchBaseQueryError(errorObj)) {
                        if (errorObj.data.errorCode) {
                            switch (errorObj.data.errorCode) {
                                case 'keyword.upload.limit.banner':
                                    throw new Error(
                                        translate('keyword.upload.limit.banner', { limit: bannerLimit }) as string,
                                    );
                                case 'keyword.upload.limit.search':
                                    throw new Error(
                                        translate('keyword.upload.limit.search', { limit: searchLimit }) as string,
                                    );
                                case 'error.upload.missing_headers':
                                    throw new Error(translate('error.upload.missing_headers') as string);
                                default:
                                    throw new Error(translate('error.generic') as string);
                            }
                        }
                    }
                    throw new Error(translate('error.generic') as string);
                }

                goBack();
            } catch (error) {
                toast({
                    title: (error as Error).message,
                    status: 'error',
                    isClosable: true,
                });
            }
        };

        const handleDownloadTemplate = async () => {
            try {
                const {
                    data: res,
                    isError: isExportError,
                    error,
                } = await exportKeywords({
                    types: keywordsType === 'both' ? ['search', 'banner'] : [keywordsType],
                    isTemplate: true,
                });

                if (isExportError || !res) {
                    if (isFetchBaseQueryError(error)) {
                        const errorMsg = error.data as string;
                        throw new Error(errorMsg);
                    }

                    throw new Error(translate('keyword.upload.error.downloadTemplate') as string);
                }

                const blob = new Blob([res], {
                    type: EXCEL_FILE_TYPE,
                });

                FileSaver.saveAs(blob, translate('keyword.upload.templateFileName') as string);
            } catch (error) {
                toast({
                    title: (error as Error).message,
                    status: 'error',
                    isClosable: true,
                });
            }
        };

        const renderOverwriteModel = () => {
            return (
                <Modal isCentered initialFocusRef={overwriteInitialRef} isOpen={isOpen} onClose={onClose}>
                    <ModalOverlay />
                    <ModalContent>
                        <ModalHeader tabIndex={-1} ref={overwriteInitialRef}>
                            <Text as="h3">
                                {uploadMethod === 'overwrite'
                                    ? translate('keyword.upload.modal.overwriteHeader')
                                    : translate('keyword.upload.modal.disableHeader')}
                            </Text>
                        </ModalHeader>
                        <ModalCloseButton mt="2" isDisabled={isLoading} />
                        <ModalBody fontSize="sm">
                            {uploadMethod === 'overwrite' ? (
                                <>
                                    <Text>{translate('keyword.upload.modal.overwriteDesc1')}</Text>
                                    <Text>{translate('keyword.upload.modal.overwriteDesc2')}</Text>
                                </>
                            ) : (
                                <Text>{translate('keyword.upload.modal.disableDesc')}</Text>
                            )}
                        </ModalBody>

                        <ModalFooter>
                            <Button isDisabled={isLoading} variant="outline" size="sm" mr={3} onClick={onClose}>
                                {translate('keyword.upload.modal.cancel')}
                            </Button>
                            <Button
                                onClick={intentionalyFloatingPromiseReturn(handleUploadKeywords)}
                                isLoading={isLoading}
                                colorScheme="red"
                                size="sm"
                            >
                                {uploadMethod === 'overwrite'
                                    ? translate('keyword.upload.modal.overwrite')
                                    : translate('keyword.upload.modal.apply')}
                            </Button>
                        </ModalFooter>
                    </ModalContent>
                </Modal>
            );
        };

        const handleUploadChecker = async () => {
            if (uploadMethod === 'keep') {
                await handleUploadKeywords();
            } else {
                onOpen();
            }
        };

        return (
            <Flex w="40%" justifyContent="flex-start" direction="column" textAlign="start">
                <Text fontSize="md">{translate('keyword.upload.typeSelection')}</Text>
                <Select bg="white" my={2} value={keywordsType} onChange={handleChangeKeywordType}>
                    <option value="search">{translate('keyword.upload.search')}</option>
                    <option value="banner">{translate('keyword.upload.banner')}</option>
                    <option value="both">{translate('keyword.upload.both')}</option>
                </Select>
                <Text mb={8} color="gray.500">
                    <chakra.span fontWeight="700">{translate('keyword.upload.tip')}</chakra.span>
                    <chakra.span
                        {...linkStyle}
                        mx={1}
                        onClick={intentionalyFloatingPromiseReturn(handleDownloadTemplate)}
                    >
                        {translate('keyword.upload.templateBtn')}
                    </chakra.span>
                    {translate('keyword.upload.startingPoint')}
                </Text>
                <Text fontSize="md">{translate('keyword.upload.method')}</Text>
                <Select mb={8} mt={2} bg="white" value={uploadMethod} onChange={handleChangeUploadMethod}>
                    <option value="keep">{translate('keyword.upload.method.keep')}</option>
                    <option value="overwrite">{translate('keyword.upload.method.overwrite')}</option>
                </Select>
                <Text fontSize="md" pt={4} mb={2}>
                    {translate('keyword.upload.file')}
                </Text>
                {(keywordsType === 'banner' || keywordsType === 'both') && (
                    <Text color="gray.500">
                        {translate('keyword.upload.limit.banner', { limit: bannerLimit })}
                        {/* the master keywords can't exceed {masterBannersLimit} */}
                    </Text>
                )}
                {(keywordsType === 'search' || keywordsType === 'both') && (
                    <Text color="gray.500">
                        {translate('keyword.upload.limit.search', { limit: searchLimit })}
                        {/* the master keywords can't exceed {masterSearchLimit} */}
                    </Text>
                )}
                <Box py={4}>
                    <MDropZone chosenFile={uploadedFile} handleChangeFile={handleChangeFile} />
                </Box>
                <Flex mt="24px" justifyContent="space-between">
                    <Button
                        isDisabled={!uploadedFile}
                        rightIcon={<Icon as={UploadIcon} />}
                        isLoading={isLoading}
                        onClick={intentionalyFloatingPromiseReturn(handleUploadChecker)}
                        colorScheme="blue"
                        bg="blueMain"
                    >
                        {translate('keyword.upload.apply')}
                    </Button>
                </Flex>
                {renderOverwriteModel()}
            </Flex>
        );
    };

    return (
        <Box>
            <PageHeader
                pageTitle={translate('keyword.upload.title') as string}
                icon={{ name: 'arrowBack', props: { onClick: goBack } }}
            />
            <Box px="32px">
                <Flex justifyContent="start">
                    <Heading as="h2" fontWeight="semibold" fontSize="18px" mb="18px">
                        {translate('keyword.upload.header')}
                    </Heading>
                </Flex>
                <Flex fontSize="sm" direction="row">
                    {renderDescriptionSection()}
                </Flex>
            </Box>
        </Box>
    );
};

export default KeywordsUpload;
